import { useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Field } from "@/app/tool/Options";
import { usePlans, PlanGrid, IntervalToggle } from "@/components/PlanCards";
import { PlanAction } from "@/app/PlanActions";

function mountProviderHtml(host, html) {
  // Only HTML returned by iyzico via our backend is mounted here — never user input.
  host.replaceChildren();
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  [...tpl.content.childNodes].forEach((n) => {
    if (n.nodeName === "SCRIPT") {
      const s = document.createElement("script");
      [...n.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.text = n.text;
      host.appendChild(s);
    } else host.appendChild(n.cloneNode(true));
  });
}

function CheckoutForm({ plan, interval }) {
  const { t, errMsg } = useI18n();
  const [f, setF] = useState({ name: "", surname: "", gsm_number: "+90", identity_number: "", address: "", city: "", zip_code: "" });
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);
  const host = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { ...f, plan_id: plan.id, interval });
      mountProviderHtml(host.current, data.checkout_form_content);
      setMounted(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const inp = (k, props = {}) => <Input required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`checkout-${k}-input`} {...props} />;
  return (
    <div className="panel p-6" data-testid="checkout-form">
      {!mounted && (
        <form onSubmit={submit} className="space-y-4">
          <h3 className="font-semibold">{t("sub.form")}</h3>
          <p className="text-sm text-emerald-400" data-testid="checkout-selected-plan">{t("plans.chosen", { name: plan.name, interval: t(`plans.${interval}`) })}</p>
          <p className="text-xs text-neutral-400">{t("sub.formNote")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t("sub.firstName")}>{inp("name")}</Field>
            <Field label={t("sub.lastName")}>{inp("surname")}</Field>
            <Field label={t("sub.phone")}>{inp("gsm_number", { pattern: "\\+?[0-9]{10,15}" })}</Field>
            <Field label={t("sub.identity")}>{inp("identity_number", { pattern: "[0-9]{11}", inputMode: "numeric", autoComplete: "off" })}</Field>
          </div>
          <Field label={t("sub.address")}>{inp("address", { minLength: 5 })}</Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("sub.city")}>{inp("city")}</Field>
            <Field label={`${t("sub.zip")} (${t("common.optional")})`}><Input value={f.zip_code} onChange={(e) => setF({ ...f, zip_code: e.target.value })} data-testid="checkout-zip_code-input" /></Field>
          </div>
          <button disabled={busy} className="btn-brand rounded-full px-6 py-2.5 font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="checkout-continue-btn">
            {busy && <Loader2 size={16} className="animate-spin" />}{t("sub.continue")}
          </button>
        </form>
      )}
      <div ref={host} id="iyzipay-checkout-form" className="responsive" data-testid="iyzico-form-host" />
    </div>
  );
}

export default function Subscription() {
  const { t, fmtDate, errMsg } = useI18n();
  const { ent, reloadEnt } = useOutletContext();
  const data = usePlans();
  const [chosen, setChosen] = useState(null);
  const [ivState, setIv] = useState("monthly");
  const [params] = useSearchParams();
  const nav = useNavigate();
  const result = params.get("result");
  useEffect(() => { reloadEnt(); }, [reloadEnt]);
  const sub = ent?.subscription;
  const status = sub?.status || "none";
  const locked = !!(ent?.active && sub);
  const interval = locked ? sub.interval || "monthly" : ivState;
  const plans = data?.items || [];
  const curOrder = plans.find((p) => p.id === (sub?.plan_id || "baslangic"))?.sort_order ?? 0;
  const anyPayable = plans.some((p) => p.payable?.monthly || p.payable?.yearly);
  const cancel = async () => {
    try { await api.post("/billing/cancel"); reloadEnt(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-6xl" data-testid="subscription-page">
      <PageHeader title={t("sub.title")} />
      {result && (
        <div className={`panel p-4 mb-6 flex items-center gap-3 ${result === "success" ? "border-emerald-700" : "border-red-800"}`} data-testid="payment-result-banner">
          {result === "success" ? <ShieldCheck className="text-emerald-400" /> : <AlertTriangle className="text-red-400" />}
          <p className="text-sm flex-1">{t(result === "success" ? "sub.success" : "sub.failed")}</p>
          <button onClick={() => nav("/app/subscription", { replace: true })} className="text-xs text-neutral-400">{t("common.close")}</button>
        </div>
      )}
      <div className="mb-8">
        <div className="panel p-6" data-testid="subscription-status-card">
          <p className="text-xs text-neutral-400">{t("sub.status")}</p>
          <p className="text-2xl font-semibold mt-1" data-testid="subscription-status">{t(`sub.states.${status}`)}</p>
          {ent?.active && sub?.plan_name && <p className="text-sm mt-2" data-testid="subscription-plan-name">{t("sub.plan")}: <span className="font-semibold">{sub.plan_name}</span> · {t(`plans.${sub.interval || "monthly"}`)}</p>}
          {sub?.current_period_end && <p className="text-sm text-neutral-400 mt-2">{t("sub.period")}: {fmtDate(sub.current_period_end)}</p>}
          {status === "canceled" && <p className="text-xs text-amber-300 mt-2">{t("sub.canceledNote", { date: fmtDate(sub.current_period_end) })}</p>}
          {status === "unpaid" && <p className="text-xs text-red-300 mt-2">{t("sub.unpaidNote")}</p>}
          {ent?.active && <p className="text-sm mt-3">{t("app.quotaLeft", { used: ent.used, quota: ent.quota })}</p>}
          {!ent?.active && <p className="text-sm mt-3 text-neutral-300" data-testid="trial-info">{t("sub.trialInfo", { n: ent?.trial_remaining ?? 0 })}</p>}
          {["active", "unpaid"].includes(status) && (
            <AlertDialog>
              <AlertDialogTrigger asChild><button className="mt-5 text-sm text-red-400 hover:underline" data-testid="cancel-subscription-btn">{t("sub.cancel")}</button></AlertDialogTrigger>
              <AlertDialogContent className="bg-[var(--bg-1)] border-[var(--line)]">
                <AlertDialogHeader><AlertDialogTitle>{t("sub.cancelConfirm")}</AlertDialogTitle></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.no")}</AlertDialogCancel>
                  <AlertDialogAction onClick={cancel} className="bg-red-600 hover:bg-red-700" data-testid="cancel-subscription-confirm">{t("common.yes")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-lg font-semibold">{locked ? t("plans.change") : t("sub.plan")}</h2>
        <IntervalToggle value={interval} onChange={(v) => { setIv(v); setChosen(null); }} disabled={locked} />
      </div>
      {locked && <p className="text-xs text-neutral-400 -mt-3 mb-5" data-testid="plan-interval-locked">{sub.provider_ref ? t("plans.intervalLocked", { interval: t(`plans.${interval}`) }) : t("plans.manualOnly")}</p>}
      {data && <PlanGrid plans={plans} interval={interval} action={(p, price) => (
        <PlanAction p={p} price={price} interval={interval} ent={ent} curOrder={curOrder} chosen={chosen} onChoose={setChosen} reloadEnt={reloadEnt} />
      )} />}
      <p className="text-[11px] text-neutral-500 mt-4">{interval === "yearly" && `${t("plans.yearlyNote")} `}{t(interval === "yearly" ? "plans.renewYearly" : "landing.renewNote")}</p>
      {!ent?.active && data && (anyPayable ? (chosen && <div className="mt-8 max-w-2xl"><CheckoutForm key={`${chosen.id}-${interval}`} plan={chosen} interval={interval} /></div>) : (
        <div className="panel p-5 mt-8 text-sm text-amber-300 flex gap-3" data-testid="payments-not-configured"><AlertTriangle size={18} className="shrink-0" />{t(data.payments_configured ? "plans.notReady" : "sub.notConfigured")}</div>
      ))}
    </div>
  );
}
